import { ApiResult, HttpClient } from "../http.js";
import {
  TimeplanEntryKind,
  TimeplanPhaseDto,
  TimeplanPhaseNoteDto,
  TimeplanPhaseStatusWire,
} from "../types/timeplan.js";

export interface CreateTimeplanPhasePayload {
  title: string;
  /** Defaults to "phase" server-side when omitted. */
  kind?: TimeplanEntryKind;
  description?: string | null;
  /** AssignmentTradeType slug (e.g. "carpenter"). */
  trade?: string | null;
  estimatedDays?: number | null;
  startDate?: string | null;
  endDate?: string | null;
  partnerInvitationId?: string | null;
  partnerCompanyId?: string | null;
  partnerCompanyName?: string | null;
  color?: string | null;
  dependsOn?: string[] | null;
}

/**
 * Partial update — every field is optional and an omitted field leaves the persisted value
 * untouched (parity with the Firestore `updateDoc` editor save).
 */
export interface UpdateTimeplanPhasePayload {
  title?: string | null;
  description?: string | null;
  trade?: string | null;
  estimatedDays?: number | null;
  startDate?: string | null;
  endDate?: string | null;
  status?: TimeplanPhaseStatusWire | null;
  partnerInvitationId?: string | null;
  partnerCompanyId?: string | null;
  partnerCompanyName?: string | null;
  color?: string | null;
  dependsOn?: string[] | null;
}

export interface TimeplanPhaseOrderInput {
  phaseId: string;
  sortOrder: number;
}

export interface ReorderTimeplanPhasesPayload {
  orders: TimeplanPhaseOrderInput[];
}

/**
 * Create-note payload. `text` is required; the author display fields are optional snapshots
 * (the author subject id is taken server-side from the authenticated caller). Partners author
 * under their own company, so they pass `authorCompanyId`/`authorCompanyName`.
 */
export interface CreateTimeplanPhaseNotePayload {
  text: string;
  authorName?: string | null;
  authorCompanyId?: string | null;
  authorCompanyName?: string | null;
}

/**
 * Authenticated CRUD for a project's timeplan entries (phases + blockers). Replaces direct
 * Firestore access to `PROJECTS/{projectId}/TIMEPLAN_PHASES`.
 */
export class TimeplanApi {
  constructor(private readonly http: HttpClient) {}

  /** Lists every phase + blocker for the project, ordered by sort order. */
  listPhases(projectId: string): Promise<ApiResult<TimeplanPhaseDto[]>> {
    return this.http.get<TimeplanPhaseDto[]>(
      `/api/projects/${encodeURIComponent(projectId)}/timeplan/phases`,
    );
  }

  /** Creates a phase or blocker; lazily provisions the timeplan on first use. */
  createPhase(
    projectId: string,
    payload: CreateTimeplanPhasePayload,
  ): Promise<ApiResult<TimeplanPhaseDto>> {
    return this.http.post<TimeplanPhaseDto>(
      `/api/projects/${encodeURIComponent(projectId)}/timeplan/phases`,
      { body: payload },
    );
  }

  updatePhase(
    projectId: string,
    phaseId: string,
    payload: UpdateTimeplanPhasePayload,
  ): Promise<ApiResult<TimeplanPhaseDto>> {
    return this.http.patch<TimeplanPhaseDto>(
      `/api/projects/${encodeURIComponent(projectId)}/timeplan/phases/${encodeURIComponent(phaseId)}`,
      { body: payload },
    );
  }

  reorderPhases(
    projectId: string,
    payload: ReorderTimeplanPhasesPayload,
  ): Promise<ApiResult<string>> {
    return this.http.post<string>(
      `/api/projects/${encodeURIComponent(projectId)}/timeplan/phases/reorder`,
      { body: payload },
    );
  }

  deletePhase(projectId: string, phaseId: string): Promise<ApiResult<string>> {
    return this.http.delete<string>(
      `/api/projects/${encodeURIComponent(projectId)}/timeplan/phases/${encodeURIComponent(phaseId)}`,
    );
  }

  /** Lists the notes on a phase (oldest first). Replaces the Firestore `…/NOTES` subcollection read. */
  listPhaseNotes(
    projectId: string,
    phaseId: string,
  ): Promise<ApiResult<TimeplanPhaseNoteDto[]>> {
    return this.http.get<TimeplanPhaseNoteDto[]>(
      `/api/projects/${encodeURIComponent(projectId)}/timeplan/phases/${encodeURIComponent(phaseId)}/notes`,
    );
  }

  /** Appends a note to a phase. */
  createPhaseNote(
    projectId: string,
    phaseId: string,
    payload: CreateTimeplanPhaseNotePayload,
  ): Promise<ApiResult<TimeplanPhaseNoteDto>> {
    return this.http.post<TimeplanPhaseNoteDto>(
      `/api/projects/${encodeURIComponent(projectId)}/timeplan/phases/${encodeURIComponent(phaseId)}/notes`,
      { body: payload },
    );
  }
}
